import { createContext, useContext, useEffect, useState, useRef, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useLocation } from "react-router-dom";

interface Profile {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  referral_code: string;
  referred_by: string | null;
  is_paid: boolean;
  is_admin: boolean;
  avatar_url: string | null;
  created_at: string;
}

interface Wallet {
  id: string;
  user_id: string;
  balance: number;
  pending_balance: number;
  total_earned: number;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  wallet: Wallet | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string, phone: string, referralCode?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const previousUserRef = useRef<string | null>(null);

  const fetchProfile = async (userId: string, isNewSignIn = false) => {
    const { data: profileData } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();

    if (profileData) {
      setProfile(profileData);

      // Only redirect after a new sign-in from auth pages (login/signup)
      // Skip if a ?redirect param exists — the page handles it directly
      const authPages = ['/login', '/signup', '/forgot-password', '/reset-password'];
      const currentPath = window.location.pathname;
      const hasRedirectParam = new URLSearchParams(window.location.search).has('redirect');
      if (isNewSignIn && authPages.includes(currentPath) && !hasRedirectParam) {
        if (!profileData.is_paid) {
          navigate("/payment");
        } else {
          navigate("/dashboard");
        }
      }

      const { data: walletData } = await supabase
        .from("wallets")
        .select("*")
        .eq("user_id", userId)
        .single();

      if (walletData) {
        setWallet(walletData);
      }
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  useEffect(() => {
    // Set up auth state listener BEFORE checking session
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // JWT expiration validation: reject expired tokens immediately
        if (session?.expires_at) {
          const expiresAt = session.expires_at * 1000; // Convert to ms
          if (Date.now() >= expiresAt) {
            console.warn('Session token expired, signing out.');
            await supabase.auth.signOut();
            setSession(null);
            setUser(null);
            setProfile(null);
            setWallet(null);
            setLoading(false);
            return;
          }
        }

        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          const isNewSignIn = previousUserRef.current !== session.user.id && 
            (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED');
          previousUserRef.current = session.user.id;
          // Use setTimeout to avoid potential race conditions
          setTimeout(() => fetchProfile(session.user.id, event === 'SIGNED_IN'), 0);
        } else {
          previousUserRef.current = null;
          setProfile(null);
          setWallet(null);
        }
        setLoading(false);
      }
    );

    // Then check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      // Validate session token expiration on initial load
      if (session?.expires_at) {
        const expiresAt = session.expires_at * 1000;
        if (Date.now() >= expiresAt) {
          supabase.auth.signOut();
          setLoading(false);
          return;
        }
      }

      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      }
      setLoading(false);
    });

    // Periodic session health check — auto-logout if token expires between refreshes
    const sessionCheckInterval = setInterval(async () => {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession && user) {
        setUser(null);
        setSession(null);
        setProfile(null);
        setWallet(null);
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => {
      subscription.unsubscribe();
      clearInterval(sessionCheckInterval);
    };
  }, []);

  const signUp = async (
    email: string, 
    password: string, 
    fullName: string, 
    phone: string,
    referralCode?: string
  ) => {
    try {
      // Validate referral code via secure edge function with rate limiting
      let referrerId: string | null = null;
      if (referralCode) {
        const { data, error } = await supabase.functions.invoke('validate-referral', {
          body: { referralCode: referralCode.toUpperCase() }
        });
        
        if (error) {
          console.warn('Referral validation failed:', error.message);
        } else if (data?.valid && data?.referrerId) {
          referrerId = data.referrerId;
        } else if (data?.rateLimited) {
          throw new Error('Too many referral code attempts. Please try again later.');
        }
      }

      // Use OTP-based email verification instead of link
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      });

      if (error) throw error;

      // If email confirmation is required, the user won't have a session yet.
      // Profile/wallet are created by DB trigger on auth.users insert regardless.
      // Update phone and referrer if the user record was created.
      if (data.user && (phone || referrerId)) {
        const { error: updateError } = await supabase
          .from("profiles")
          .update({
            phone: phone || null,
            referred_by: referrerId,
          })
          .eq("id", data.user.id);

        if (updateError) {
          console.warn('Failed to update profile with phone/referrer:', updateError.message);
        }

        // Create referral record if user was referred
        if (referrerId) {
          const { error: referralError } = await supabase
            .from("referrals")
            .insert({
              referrer_id: referrerId,
              referred_id: data.user.id,
            });
          
          if (referralError) {
            console.warn('Failed to create referral record:', referralError.message);
          }
        }
      }

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setWallet(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        wallet,
        loading,
        signUp,
        signIn,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
